import { useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { StatusBadge } from '@/components/StatusBadge';
import { useAuth } from '@/auth/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import type { PutawayAwaitingRow, PutawayQueueTask } from '@/api/types';

/**
 * Putaway queue: goods that were received and wait in the receiving zone ("awaiting putaway", unavailable for picking) and the PUTAWAY
 * tasks the handhelds work. The same `task` rows as on the handheld; nothing here moves stock - the move is the scanned putaway.
 * Reading needs INVENTORY.READ; creating tasks, releasing a held task and withdrawing one need PUTAWAY.WRITE.
 */
export function PutawayQueuePage(): JSX.Element {
  const { t, locale } = useLanguage();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('PUTAWAY.WRITE');
  const queue = useApi(() => endpoints.putawayQueue(), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [confirm, setConfirm] = useState<{ id: string; action: 'release' | 'cancel' } | null>(null);

  if (queue.status === 'loading') return <LoadingState />;
  if (queue.status === 'error') return <ErrorBanner error={queue.error} onRetry={queue.reload} />;
  const { awaiting, tasks } = queue.data;
  const unplanned = awaiting.reduce((n, r) => n + (r.waits_for_return ? 0 : Number(r.unplanned)), 0);
  const held = awaiting.reduce((n, r) => n + Number(r.held_overage), 0);
  const inWork = tasks.filter((x) => x.state !== 'PENDING').length;

  async function act(key: string, fn: () => Promise<unknown>, done: string): Promise<void> {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const res = await fn();
      setNotice(typeof res === 'string' ? res : done);
      setConfirm(null);
      queue.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  }

  const holder = (x: PutawayQueueTask): string =>
    x.claimed_by ? `${x.claimed_by}${x.claim_live ? '' : ` (${t('putaway.leaseLapsed')})`}` : '—';

  return (
    <div>
      <div className="page-header">
        <h1>{t('putaway.title')}</h1>
        {canWrite && (
          <button
            type="button"
            className="primary"
            disabled={busy !== null || unplanned <= 0}
            onClick={() => void act('plan', async () => { const r = await endpoints.planPutaway(); return t('putaway.planned', { n: r.created, qty: formatNumber(r.qty, locale) }); }, '')}
          >
            {t('putaway.plan')}
          </button>
        )}
      </div>
      <p className="sub">{t('putaway.subtitle')}</p>

      {notice && <div className="card success-card" role="status">{notice}</div>}
      {error !== null && <ErrorBanner error={error} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <div className="card"><strong>{formatNumber(String(unplanned), locale)}</strong><div className="sub">{t('putaway.statUnplanned')}</div></div>
        <div className="card"><strong>{tasks.length}</strong><div className="sub">{t('putaway.statTasks')}</div></div>
        <div className="card"><strong>{inWork}</strong><div className="sub">{t('putaway.statInWork')}</div></div>
        <div className="card"><strong>{formatNumber(String(held), locale)}</strong><div className="sub">{t('putaway.statHeld')}</div></div>
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('putaway.awaitingTitle')}</h2>
        {awaiting.length === 0 ? (
          <p className="sub" style={{ margin: 0 }}>{t('putaway.awaitingEmpty')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('putaway.colCell')}</th><th>{t('inventory.colSku')}</th><th>{t('inbounds.colSeller')}</th>
                <th style={{ textAlign: 'right' }}>{t('putaway.colOnHand')}</th><th style={{ textAlign: 'right' }}>{t('putaway.colHeld')}</th>
                <th style={{ textAlign: 'right' }}>{t('putaway.colPlanned')}</th><th style={{ textAlign: 'right' }}>{t('putaway.colUnplanned')}</th>
                <th>{t('putaway.colSince')}</th>
              </tr>
            </thead>
            <tbody>
              {awaiting.map((r: PutawayAwaitingRow) => (
                <tr key={`${r.location_id}-${r.sku_id}-${r.owner_id}`}>
                  <td>{r.location_code}</td>
                  <td>{r.sku_code} — {r.sku_name}{r.is_serial_tracked && <span className="badge" style={{ marginLeft: 6 }}>{t('putaway.serial')}</span>}</td>
                  <td>{r.owner_name}</td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(r.qty_on_hand, locale)}</td>
                  <td style={{ textAlign: 'right' }}>{Number(r.held_overage) > 0 ? <span className="badge warn" title={t('putaway.heldHint')}>{formatNumber(r.held_overage, locale)}</span> : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(r.planned, locale)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {formatNumber(r.unplanned, locale)}
                    {r.waits_for_return && <div className="sub">{t('putaway.waitsForReturn')}</div>}
                    {r.is_serial_tracked && Number(r.unplanned) > 0 && <div className="sub">{t('putaway.serialHint')}</div>}
                  </td>
                  <td>{formatDateTime(r.since, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card">
        <h2 style={{ fontSize: 14, marginTop: 0 }}>{t('putaway.tasksTitle')}</h2>
        {tasks.length === 0 ? (
          <p className="sub" style={{ margin: 0 }}>{t('putaway.tasksEmpty')}</p>
        ) : (
          <div className="table-scroll"><table>
            <thead>
              <tr>
                <th>{t('common.status')}</th><th>{t('putaway.colCell')}</th><th>{t('inventory.colSku')}</th><th>{t('inbounds.colSeller')}</th>
                <th style={{ textAlign: 'right' }}>{t('putaway.colDone')}</th><th>{t('putaway.colHolder')}</th><th>{t('putaway.colCreated')}</th>
                {canWrite && <th><span className="sr-only">{t('common.actions')}</span></th>}
              </tr>
            </thead>
            <tbody>
              {tasks.map((x) => (
                <tr key={x.id}>
                  <td><StatusBadge value={x.state} /></td>
                  <td>{x.location_code ?? '—'}</td>
                  <td>{x.sku_code} — {x.sku_name}</td>
                  <td>{x.owner_name ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{formatNumber(x.qty_done, locale)} / {formatNumber(x.qty_target, locale)}</td>
                  <td>{holder(x)}</td>
                  <td>{formatDateTime(x.created_at, locale)}</td>
                  {canWrite && (
                    <td>
                      {confirm?.id === x.id ? (
                        <span role="group" aria-label={t('putaway.confirmAria')}>
                          <span>{confirm.action === 'release' ? t('putaway.confirmRelease') : t('putaway.confirmCancel')} </span>
                          <button type="button" className="primary" disabled={busy !== null}
                            onClick={() => void act(`${confirm.action}-${x.id}`, () => (confirm.action === 'release' ? endpoints.releasePutawayTask(x.id) : endpoints.cancelPutawayTask(x.id)), confirm.action === 'release' ? t('putaway.released') : t('putaway.cancelled'))}>
                            {t('common.confirm')}
                          </button>{' '}
                          <button type="button" disabled={busy !== null} onClick={() => setConfirm(null)}>{t('common.cancel')}</button>
                        </span>
                      ) : (
                        <>
                          {x.claimed_by && <button type="button" onClick={() => setConfirm({ id: x.id, action: 'release' })}>{t('putaway.release')}</button>}{' '}
                          <button type="button" className="danger" onClick={() => setConfirm({ id: x.id, action: 'cancel' })}>{t('putaway.cancelTask')}</button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
